// A fonte do dado de sono deixa de ser um literal.
//
// ═════════════════════════════════════════════════════════════════════════
// O DEFEITO: O APP SE DESMENTIA NA MESMA SESSÃO
// ═════════════════════════════════════════════════════════════════════════
// A aba Log mostrava noites etiquetadas "APPLE HEALTH" — dado real, entrando,
// com o sync consertado em 20/08 (123 → 169 linhas). Ao lado, a aba Insights e
// o sheet de Configurações mostravam Apple Watch, Garmin, Oura e Whoop os
// QUATRO no mesmo balde de "Coming Soon". Quem tinha a conexão ligada lia, na
// mesma tela, que ela não existia.
//
// ⛔ A CORREÇÃO NÃO É TROCAR UM LITERAL POR OUTRO. O card da fonte nativa LÊ
// `user_connections` (provider 'apple_health' no iOS, 'health_connect' no
// Android) pelo mesmo `getConnection` que a tela de Integrações já usa. Se a
// conexão cair, este card cai junto — sem ninguém editar string.
//
// 🔴 E OS DOIS BALDES NÃO SE MISTURAM MAIS. Garmin, Oura e Whoop ficam num
// grupo separado porque integração DIRETA com eles de fato não existe — mas o
// rótulo do grupo é INSTRUÇÃO, não promessa (22/08): "entram via {hub}". O
// caminho existe HOJE (o hub do SO lê esses aparelhos); "em breve" escondia
// isso e prometia um trabalho que não está no roadmap.
//
// ⚠️ O NOME É "APPLE SAÚDE", NUNCA "APPLE WATCH". A fonte é o HealthKit, e
// chega dado de iPhone sem relógio nenhum — quem anda com o telefone no bolso
// tem passos e sono ali. Prometer relógio exclui quem já é atendido hoje.

import React from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Circle, Watch, Zap, Check, AlertTriangle, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useConnections, platformHealthProvider } from "@/hooks/useConnections";
import { metricAge } from "@/lib/health/metricAge";

interface WearableSourcesProps {
  /** Versão compacta para dentro do sheet de Configurações. */
  compact?: boolean;
}

/** Os que NÃO existem. Balde separado de propósito. */
const NOT_BUILT = [
  { name: "Garmin", icon: Activity },
  { name: "Oura", icon: Circle },
  { name: "Whoop", icon: Zap },
] as const;

const WearableSources: React.FC<WearableSourcesProps> = ({ compact = false }) => {
  const { t, tf } = useLanguage();
  const navigate = useNavigate();
  // Leitura enxuta: este card só precisa da LISTA de conexões (status + último
  // sync). Os ~7 counts de badge do hook são da tela de Conexões — pagá-los a
  // cada mount de Sleep/Fitness era desperdício puro.
  const { getConnection } = useConnections({ counts: false });

  const provider = platformHealthProvider();
  const conn = provider ? getConnection(provider) : null;
  const hubName = t(provider === "health_connect" ? "sleep.sources.healthConnect" : "sleep.sources.appleHealth");

  // Os mesmos estados que ConnectionCard distingue — não uma segunda taxonomia.
  const status = conn?.status ?? null;
  const connected = status === "active";
  const needsAttention = status === "expired" || status === "error" || status === "revoked";
  const pending = status === "pending";

  // Idade do último sync pelo MESMO metricAge da tela de métricas, com as
  // MESMAS chaves de i18n. Zero vocabulário novo para dizer "há 3 dias".
  const syncLabel = (() => {
    if (!conn?.last_sync_at) return t("sleep.sources.neverSynced");
    const { days } = metricAge(conn.last_sync_at, Date.now());
    const when =
      days === 0 ? t("health.age.today")
      : days === 1 ? t("health.age.yesterday")
      : tf("health.age.daysAgo", { days: String(days) });
    return tf("sleep.sources.lastSync", { when });
  })();

  const StatusIcon = connected ? Check : needsAttention ? AlertTriangle : pending ? Loader2 : null;
  const statusColor = connected
    ? "hsl(142 60% 50%)"
    : needsAttention
      ? "hsl(0 72% 60%)"
      : "hsl(38 92% 62%)";

  const ctaKey = connected
    ? "sleep.sources.manage"
    : needsAttention
      ? "sleep.sources.reconnect"
      : pending
        ? "sleep.sources.finishSetup"
        : "sleep.sources.connect";

  return (
    <div className={compact ? "space-y-3" : "space-y-3 mb-5"}>
      {/* ── A FONTE REAL ────────────────────────────────────────────────── */}
      <div
        className="rounded-2xl p-4"
        style={{
          background: "linear-gradient(145deg, hsl(220 40% 9%), hsl(225 35% 6%))",
          border: `1px solid ${connected ? "hsl(142 60% 50% / 0.28)" : "hsl(220 50% 35% / 0.30)"}`,
        }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "hsl(220 40% 12%)", border: "1px solid hsl(220 50% 25% / 0.30)" }}
          >
            <Watch size={16} style={{ color: connected ? "hsl(142 60% 55%)" : "hsl(218 14% 62%)" }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[13px] font-semibold text-white truncate">{hubName}</p>
            <p className="text-[10px] font-body truncate" style={{ color: "hsl(218 14% 58%)" }}>
              {provider === null
                ? t("sleep.sources.webOnly")
                : connected
                  ? syncLabel
                  : needsAttention
                    ? t("sleep.sources.needsAttention")
                    : pending
                      ? t("sleep.sources.pending")
                      : t("sleep.sources.notConnected")}
            </p>
          </div>
          {StatusIcon && (
            <StatusIcon
              size={16}
              className={pending ? "animate-spin shrink-0" : "shrink-0"}
              style={{ color: statusColor }}
            />
          )}
        </div>

        {/* Na web não existe leitura nativa — dizer isso é melhor que oferecer
            um botão que não faz nada (mesma regra de HealthMetrics). */}
        {provider !== null && (
          <button
            type="button"
            onClick={() => navigate("/settings/connections")}
            className="w-full mt-3 py-2 rounded-xl font-display text-[12px] font-semibold active:scale-[0.98] transition-transform"
            style={{
              background: connected ? "hsl(220 40% 14%)" : "linear-gradient(135deg, hsl(220 70% 52%), hsl(220 70% 42%))",
              color: connected ? "hsl(218 14% 78%)" : "#fff",
              border: connected ? "1px solid hsl(220 50% 30% / 0.35)" : "none",
            }}
          >
            {t(ctaKey)}
          </button>
        )}
      </div>

      {/* ── SEM INTEGRAÇÃO DIRETA — MAS COM CAMINHO ─────────────────────── */}
      {/* O rótulo é instrução ("entram via {hub}"), não "em breve": direta não
          existe nem está prometida; o que existe HOJE é o hub do SO lendo
          esses aparelhos. Na web o próprio título já aponta os dois hubs. */}
      <div>
        <p
          className="text-[9px] font-display tracking-[0.16em] uppercase mb-2"
          style={{ color: "hsl(218 14% 45%)" }}
        >
          {provider !== null
            ? tf("sleep.sources.viaHubTitle", { hub: hubName })
            : t("sleep.sources.viaHubTitleWeb")}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {NOT_BUILT.map((w) => (
            <div
              key={w.name}
              className="py-2.5 rounded-xl flex flex-col items-center gap-1"
              style={{ background: "hsl(220 35% 10%)", border: "1px solid hsl(220 50% 30% / 0.22)" }}
            >
              <w.icon size={14} style={{ color: "hsl(218 14% 48%)" }} />
              <span className="text-[9px] font-display" style={{ color: "hsl(218 14% 52%)" }}>
                {w.name}
              </span>
            </div>
          ))}
        </div>
        {/* Verdade que faltava dos dois lados: sem integração direta, mas o hub
            do SO recebe esses aparelhos — se o dispositivo escreve lá, o VYV
            já lê. Na web a MESMA instrução é o que há para dizer (o app nativo
            é quem lê o hub), então a linha renderiza sempre — só muda a frase. */}
        <p className="text-[9px] font-body mt-2 leading-snug" style={{ color: "hsl(218 14% 48%)" }}>
          {provider !== null
            ? tf("sleep.sources.viaHub", { hub: hubName })
            : t("sleep.sources.viaHubWeb")}
        </p>
      </div>
    </div>
  );
};

export default WearableSources;
